import { useReactiveVar } from '@apollo/client';
import { conversationsVar, selectedConversationVar, showingChatBoxVar } from 'apollo/store';
import { IconCheck, IconCloseCircle, IconMessageCircle } from 'components/icons';
import { useAuthenticationContext } from 'components/providers/authentication-provider';
import { SITE_MAP } from 'globalConstants';
import {
  ACCEPT_FRIEND_REQUEST,
  AcceptFriendRequestData,
  AcceptFriendRequestVariables,
  DENY_FRIEND_REQUEST,
  DenyFriendRequestData,
  DenyFriendRequestVariables,
} from 'graphql/main/mutations';
import { Conversation } from 'graphql/main/queries';
import { Notification } from 'graphql/main/queries/temp/get-list-notification';
import { useFlexMutation } from 'hooks';
import { filter, find, includes, isEqual, map } from 'lodash';
import { useRouter } from 'next/router';
import queryString from 'query-string';
import { MouseEvent } from 'react';

interface Props {
  notification?: Notification;
}

const NewFriendRequest = ({ notification }: Props) => {
  const { payload } = notification ?? {};
  const { currentUser } = useAuthenticationContext() ?? {};
  const { asPath, push } = useRouter() ?? {};
  const conversations = useReactiveVar(conversationsVar);
  const [denyFriendRequest] = useFlexMutation<DenyFriendRequestData, DenyFriendRequestVariables>(
    'main',
    DENY_FRIEND_REQUEST,
  );
  const [accpectFriendRequest] = useFlexMutation<
    AcceptFriendRequestData,
    AcceptFriendRequestVariables
  >('main', ACCEPT_FRIEND_REQUEST);

  const accept = async (event: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if ((payload as any)?.requestId) {
      await accpectFriendRequest({
        variables: {
          requestId: (payload as any)?.requestId,
        },
      });
    }
  };
  const deny = async (event: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if ((payload as any)?.requestId) {
      await denyFriendRequest({
        variables: {
          requestId: (payload as any)?.requestId,
        },
      });
    }
  };
  const message = (event: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (!currentUser) {
      push({
        pathname: `/${SITE_MAP.SIGN_IN.INDEX}`,
        query: queryString.stringify({ redirect: asPath }, { encode: false }),
      });
    } else {
      const foundConversation = find(
        conversations,
        (conversation) =>
          !conversation.isGroup &&
          includes(
            map(conversation.participants, (participant) => participant.userId),
            currentUser?.id,
          ) &&
          includes(
            map(conversation.participants, (participant) => participant.userId),
            (payload as any)?.user?.id,
          ),
      );
      if (foundConversation) {
        selectedConversationVar(foundConversation);
      } else {
        const newConversation: Conversation = {
          name: (payload as any)?.user?.fullName,
          participants: [
            {
              userId: currentUser?.id,
              avatar: currentUser?.avatar,
              fullName: currentUser?.fullName,
            },
            {
              userId: (payload as any)?.user?.id,
              avatar: (payload as any)?.user?.avatar ?? undefined,
              fullName: (payload as any)?.user?.fullName,
            },
          ],
        };
        conversationsVar([
          newConversation,
          ...filter(conversations, (conversation) => Boolean(conversation.id)),
        ]);
        selectedConversationVar(newConversation);
      }
      showingChatBoxVar(true);
    }
  };

  return isEqual((payload as any)?.friendStatus, 'REQUESTED') ? (
    <div className='flex w-full space-x-[12px]'>
      <button
        type='button'
        className='flex h-[40px] items-center justify-center space-x-[6px] rounded-[6px] text-paper bg-primary transition duration-[200ms] ease-in-out hover:bg-primary-light w-[120px]'
        onClick={accept}
      >
        <IconCheck className='h-[20px] w-[20px]' />
        <span className='font-[600]'>Đồng ý</span>
      </button>
      <button
        type='button'
        className='flex h-[40px] items-center justify-center space-x-[6px] border border-alert rounded-[6px] w-[120px]'
        onClick={deny}
      >
        <IconCloseCircle className='h-[20px] w-[20px]' />
        <span className='font-[600]'>Từ chối</span>
      </button>
    </div>
  ) : isEqual((payload as any)?.friendStatus, 'ACCEPTED') ? (
    <div className='flex-flex-full space-y-[4px]'>
      <span className='text-[12px] leading-[15px] text-text2'>
        Lời mời kết bạn đã được chấp nhận
      </span>
      <button
        type='button'
        className='flex h-[40px] items-center justify-center space-x-[6px] rounded-[6px] bg-tertiary transition duration-[200ms] ease-in-out hover:bg-tertiary-light w-[120px]'
        onClick={message}
      >
        <IconMessageCircle className='h-[24px] w-[24px]' />
        <span className='font-[600]'>Nhắn tin</span>
      </button>
    </div>
  ) : (
    <span className='text-[12px] leading-[15px] text-text2'>Đã từ chối lời mời kết bạn</span>
  );
};

export default NewFriendRequest;
